import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  MagnifyingGlassIcon, 
  LinkIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  ClipboardIcon,
  ChartBarIcon,
  CalendarIcon,
  UserIcon
} from '@heroicons/react/24/outline'
import { adminApiService } from '../../../shared/services/adminApi'
import { SystemStatusCard, AdminHeader } from '../../../shared/components'
import toast from 'react-hot-toast'
import type { SystemHealth } from '../../../shared/services/adminApi'

interface LinkData {
  _id: string
  shortCode: string
  originalUrl: string
  customAlias?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
  expiresAt?: string
  clicks: number
  lastClickAt?: string
  userId: {
    _id: string
    email: string
    name?: string
    plan: string
  }
  tags: string[]
  description?: string
  qrCode?: string
  isPasswordProtected: boolean
  isCustomDomain: boolean
  domain?: string
}

interface LinkStats {
  totalLinks: number
  activeLinks: number
  totalClicks: number
  linksToday: number
  clicksToday: number
  avgClicksPerLink: number
}

interface LinkDeletionPreview {
  link: {
    id: string
    shortCode: string
    originalUrl: string
    createdAt: string
    clicks: number
  }
  deletionPreview: {
    clicksToDelete: number
    analyticsToDelete: number
    dateRanges: {
      firstClick: string | null
      lastClick: string | null
    }
  }
  warning: string
}

export default function LinkManagementPage() {
  const [links, setLinks] = useState<LinkData[]>([])
  const [stats, setStats] = useState<LinkStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [sortBy, setSortBy] = useState<'createdAt' | 'clicks' | 'lastClickAt'>('createdAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [selectedLinks, setSelectedLinks] = useState<string[]>([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [linkToDelete, setLinkToDelete] = useState<string | null>(null)
  const [deletionPreview, setDeletionPreview] = useState<LinkDeletionPreview | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null)
  const [isRefreshingStatus, setIsRefreshingStatus] = useState(false)

  const loadLinks = async () => {
    try {
      setLoading(true)
      const data = await adminApiService.getLinks({ 
        page, 
        limit: 20, 
        search: search || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        sortBy,
        sortOrder
      })
      setLinks(data.links)
      setTotalPages(data.pagination.totalPages)
      setStats(data.stats)
    } catch (error) {
      console.error('Failed to load links:', error)
      toast.error('Failed to load links')
    } finally {
      setLoading(false)
    }
  }

  const loadSystemHealth = async () => {
    try {
      const healthData = await adminApiService.getSystemHealth()
      setSystemHealth(healthData)
    } catch (error) {
      console.error('Failed to load system health:', error)
    }
  }

  const handleRefreshAll = async () => {
    try {
      setIsRefreshingStatus(true)
      await Promise.all([
        loadLinks(),
        loadSystemHealth()
      ])
      toast.success('Data refreshed successfully')
    } catch (error) {
      console.error('Failed to refresh data:', error)
      toast.error('Failed to refresh data')
    } finally {
      setIsRefreshingStatus(false)
    }
  }

  useEffect(() => {
    loadLinks()
    loadSystemHealth()
  }, [page, search, statusFilter, sortBy, sortOrder])

  const handleStatusChange = async (linkId: string, isActive: boolean) => {
    try {
      await adminApiService.updateLinkStatus(linkId, isActive)
      await loadLinks()
      toast.success(`Link ${isActive ? 'activated' : 'deactivated'} successfully`)
    } catch (error) {
      console.error('Failed to update link status:', error)
      toast.error('Failed to update link status')
    }
  }

  const handleDeleteClick = async (linkId: string) => {
    try {
      const preview = await adminApiService.getLinkDeletionPreview(linkId)
      setDeletionPreview(preview)
      setLinkToDelete(linkId)
      setShowDeleteModal(true)
    } catch (error) {
      console.error('Failed to get deletion preview:', error)
      toast.error('Failed to get deletion preview')
    }
  }

  const handleConfirmDelete = async () => {
    if (!linkToDelete) {return}

    try {
      setIsDeleting(true)
      const result = await adminApiService.deleteLink(linkToDelete)
      
      toast.success(
        `Link deleted successfully. Removed ${result.deletedCounts.clicks} clicks and ${result.deletedCounts.analytics} analytics records.`
      )
      
      setShowDeleteModal(false)
      setLinkToDelete(null)
      setDeletionPreview(null)
      await loadLinks()
    } catch (error: any) {
      console.error('Failed to delete link:', error)
      toast.error(error.response?.data?.message || 'Failed to delete link')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedLinks.length === 0) {
      toast.error('Please select links to delete')
      return
    }

    if (!confirm(`Are you sure you want to delete ${selectedLinks.length} links? This action cannot be undone.`)) {
      return
    }

    try {
      setIsDeleting(true)
      const result = await adminApiService.bulkDeleteLinks(selectedLinks)
      
      toast.success(
        `${result.deletedCounts.links} links deleted successfully. Removed ${result.deletedCounts.clicks} clicks and ${result.deletedCounts.analytics} analytics records.`
      )
      
      setSelectedLinks([])
      await loadLinks()
    } catch (error: any) {
      console.error('Failed to bulk delete links:', error)
      toast.error(error.response?.data?.message || 'Failed to delete links')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleBulkStatusChange = async (isActive: boolean) => {
    if (selectedLinks.length === 0) {
      toast.error('Please select links to update')
      return
    }

    try {
      await adminApiService.bulkUpdateLinkStatus(selectedLinks, isActive)
      toast.success(`${selectedLinks.length} links ${isActive ? 'activated' : 'deactivated'} successfully`)
      setSelectedLinks([])
      await loadLinks()
    } catch (error: any) {
      console.error('Failed to bulk update link status:', error)
      toast.error(error.response?.data?.message || 'Failed to update link status')
    }
  }

  const handleSelectLink = (linkId: string) => {
    setSelectedLinks(prev => 
      prev.includes(linkId) 
        ? prev.filter(id => id !== linkId)
        : [...prev, linkId]
    )
  }

  const handleSelectAll = () => {
    if (selectedLinks.length === links.length) {
      setSelectedLinks([])
    } else {
      setSelectedLinks(links.map(link => link._id))
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatUrl = (url: string, maxLength: number = 50) => {
    if (url.length <= maxLength) {return url}
    return url.substring(0, maxLength) + '...'
  }

  const getFullShortUrl = (link: LinkData) => {
    if (link.isCustomDomain && link.domain) {
      return `https://${link.domain}/${link.shortCode}`
    }
    return `https://shorty.kr/${link.shortCode}`
  }
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AdminHeader 
        title="Link Management"
        subtitle="Manage shortened URLs, analytics, and access controls"
        showRefresh={true}
        onRefresh={handleRefreshAll}
        isRefreshing={isRefreshingStatus}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SystemStatusCard 
          systemHealth={systemHealth}
          onRefresh={handleRefreshAll}
          isRefreshing={isRefreshingStatus}
          className="mb-6"
        />

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <LinkIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        Total Links
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {stats.totalLinks.toLocaleString()}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-6 w-6 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                      <div className="h-2 w-2 bg-green-600 dark:bg-green-400 rounded-full"></div>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        Active Links
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {stats.activeLinks.toLocaleString()}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ChartBarIcon className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        Total Clicks
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {stats.totalClicks.toLocaleString()}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CalendarIcon className="h-6 w-6 text-orange-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        Links Today
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {stats.linksToday.toLocaleString()}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ChartBarIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        Clicks Today
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {stats.clicksToday.toLocaleString()}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ChartBarIcon className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        Avg Clicks
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {stats.avgClicksPerLink.toFixed(1)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Actions */}
        {selectedLinks.length > 0 && (
          <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-800 dark:text-blue-200">
                {selectedLinks.length} link{selectedLinks.length > 1 ? 's' : ''} selected
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={() => setSelectedLinks([])}
                  className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                >
                  Clear selection
                </button>
                <button
                  onClick={() => handleBulkStatusChange(true)}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                >
                  Activate Selected
                </button>
                <button
                  onClick={() => handleBulkStatusChange(false)}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
                >
                  Deactivate Selected
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={isDeleting}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <TrashIcon className="h-4 w-4 mr-1" />
                  {isDeleting ? 'Deleting...' : 'Delete Selected'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search links by short code, URL, or description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-')
                setSortBy(field as any)
                setSortOrder(order as any)
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="createdAt-desc">Newest First</option>
              <option value="createdAt-asc">Oldest First</option>
              <option value="clicks-desc">Most Clicks</option>
              <option value="clicks-asc">Least Clicks</option>
              <option value="lastClickAt-desc">Recent Activity</option>
            </select>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading links...</p>
            </div>
          ) : links.length === 0 ? (
            <div className="p-8 text-center">
              <LinkIcon className="h-12 w-12 text-gray-400 mx-auto" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No links found</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Try adjusting your search criteria or filters.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedLinks.length === links.length && links.length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Link
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Original URL
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Clicks
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {links.map((link, index) => (
                    <motion.tr
                      key={link._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedLinks.includes(link._id)}
                          onChange={() => handleSelectLink(link._id)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <LinkIcon className="h-5 w-5 text-gray-400" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {link.customAlias || link.shortCode}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                              {formatUrl(getFullShortUrl(link), 30)}
                              <button
                                onClick={() => copyToClipboard(getFullShortUrl(link))}
                                className="ml-2 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                title="Copy link"
                              >
                                <ClipboardIcon className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {formatUrl(link.originalUrl, 40)}
                        </div>
                        {link.description && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {formatUrl(link.description, 50)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-6 w-6">
                            <div className="h-6 w-6 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                              <UserIcon className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                            </div>
                          </div>
                          <div className="ml-2">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {link.userId?.name || link.userId?.email?.split('@')[0] || 'Unknown User'}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {link.userId?.plan || 'free'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {link.clicks.toLocaleString()}
                        </div>
                        {link.lastClickAt && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Last: {formatDate(link.lastClickAt)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          link.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                        }`}>
                          {link.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {link.isPasswordProtected && (
                          <div className="mt-1">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">
                              🔒 Protected
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(link.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => window.open(getFullShortUrl(link), '_blank')}
                            className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                            title="Visit link"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleStatusChange(link._id, !link.isActive)}
                            className={`text-sm font-medium ${
                              link.isActive
                                ? 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300'
                                : 'text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300'
                            }`}
                          >
                            {link.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={() => handleDeleteClick(link._id)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            title="Delete link"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Page {page} of {totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && deletionPreview && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800">
              <div className="mt-3">
                <div className="flex items-center">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="ml-4 text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                      Delete Link
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        You are about to permanently delete the link{' '}
                        <span className="font-medium text-gray-900 dark:text-white">
                          {deletionPreview.link.shortCode}
                        </span>
                      </p>
                      <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                        {deletionPreview.warning}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteModal(false)
                      setLinkToDelete(null)
                      setDeletionPreview(null)
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-600 dark:text-gray-200 dark:border-gray-500 dark:hover:bg-gray-700"
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    disabled={isDeleting}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDeleting ? 'Deleting...' : 'Delete Link'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
